import type { CSSProperties, ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * Proposal option list — the club's line-item picker (TW UAT round 1). A flat checklist of priced
 * options: ticking a line includes it, a deselected line is struck through and faded. No category
 * headers — the descriptions carry context. Previously inline in ProposalTab; extracted so the same
 * row renders identically wherever a proposal's lines are shown. Styling: `.opt-list` / `.opt-row`.
 */
export function OptionList({
  children,
  className,
  style,
}: {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <div className={cn("opt-list", className)} style={style}>
      {children}
    </div>
  );
}

export interface OptionRowProps {
  /** Line description (e.g. "Return flights BCN ⇄ AMS"). */
  description: string;
  /** The per-unit breakdown, already formatted (e.g. "18 × €268"). */
  qtyLabel: string;
  /** The line total, already formatted (e.g. "€4,824"). */
  amountLabel: string;
  /**
   * Selectable (the club is choosing, proposal still Sent) vs static (read-only, e.g. once accepted
   * or in the back-office preview). Static rows render `.opt-static` and never strike through.
   */
  selectable?: boolean;
  /** Whether this line is included. Ignored when not selectable (always reads as included). */
  checked?: boolean;
  onToggle?: () => void;
  /** Optional trailing node rendered after the amount (e.g. a "chosen by club" badge). */
  trailing?: ReactNode;
}

export function OptionRow({
  description,
  qtyLabel,
  amountLabel,
  selectable = false,
  checked = true,
  onToggle,
  trailing,
}: OptionRowProps) {
  // A deselected line (only meaningful while selecting) is struck through + faded via `.off`.
  const off = selectable && !checked;
  return (
    <label className={cn("opt-row", !selectable && "opt-static", off && "off")}>
      {selectable && (
        <input type="checkbox" className="opt-check" checked={checked} onChange={onToggle} />
      )}
      <span className="opt-desc">{description}</span>
      <span className="opt-qty mono">{qtyLabel}</span>
      <span className="opt-amt mono">{amountLabel}</span>
      {trailing}
    </label>
  );
}
